import React, { useState } from 'react'
import { Sparkles, Send, Loader2 } from 'lucide-react'

const AICoach = ({
  mode = 'practice',
  title = 'AI Coach',
  description = 'Describe the practice you need, and AI will design it for you.',
  labelText = 'Practice Request',
  buttonText = 'Generate Practice',
  placeholder = 'e.g., I need a 90 minute practice for U12 boys that focuses on defense and positional awareness',
  examplePrompts = [
    '• "90 minute practice for U12 focusing on passing and movement"',
    '• "60 minute defensive training session"',
    '• "U14 practice with fitness and shooting drills"',
    '• "45 minute positional awareness session"'
  ],
  onGenerate,
  getExistingItems,
  clearItems,
  setItems,
  confirmationMessage = 'This will replace all existing practice tiles with the new AI-generated practice. Are you sure you want to continue?'
}) => {
  const [prompt, setPrompt] = useState('')
  const [isLoading, setIsLoading] = useState(false)


  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim() || isLoading || !onGenerate) return

    // Check if there are existing items and warn user
    const existingItems = getExistingItems ? getExistingItems() : []
    if (existingItems && existingItems.length > 0) {
      const confirmed = window.confirm(confirmationMessage)
      if (!confirmed) {
        return // User cancelled, don't proceed
      }
    }

    setIsLoading(true)
    try {
      // Call the provided generation function
      const generatedItems = await onGenerate(prompt)
      
      // Clear existing items and add generated ones
      if (clearItems) clearItems()
      if (setItems) setItems(generatedItems)
      
      // Clear the prompt
      setPrompt('')
    } catch (error) {
      console.error(`Error generating ${mode}:`, error)
      alert(`Failed to generate ${mode}. Please try again.`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 mb-2">
          <Sparkles size={20} className="text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        <p className="text-xs text-gray-600">
          {description}
        </p>
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
              {labelText}
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (prompt.trim() && !isLoading) {
                    handleSubmit(e)
                  }
                }
              }}
              placeholder={placeholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={6}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={!prompt.trim() || isLoading || !onGenerate}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>{buttonText}</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Example Prompts</h3>
          <ul className="text-xs text-gray-600 space-y-1">
            {examplePrompts.map((example, index) => (
              <li key={index}>{example}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default AICoach

